import { useEffect, useRef } from "react";
import { TUTOR_SESSION_PHASE_STATUS } from "@/features/tutor/constants";
import { useAnnotationStore } from "@/features/tutor/store/useAnnotationStore.js";
import { useAudioStore } from "@/features/tutor/store/useAudioStore.js";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useNotepadStore } from "@/features/notepad/store/useNotepadStore";
import { applyResumeFragmentGuard } from "@/features/tutor/vo-demo/utils/fragmentGuard";
import { addResumeDebugEvent } from "@/store/useResumeDebugStore";
import { trackAiMessage } from "@/utils/mixpanel-utils";
import { debugLog } from "@/utils/debugLog";
import { useLatestRef } from "./useLatestRef";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import {
  beginSilentTurn,
  endSilentTurn,
  pauseSilentTurn,
  watchSilentTurnDrain,
} from "@/features/tutor/utils/silentTurnClock";
import { isResumePlaybackHeld } from "@/features/tutor/vo-demo/runtime/resumePlaybackHold";
import { settleTutorTurn } from "@/features/tutor/utils/tutorTurnLifecycle";
import { isFromOtherTurn } from "@/features/tutor/utils/canvasTurnScope";

export function useVoDemoRuntimeEvents({
  abruptPauseTimerRef,
  addLog,
  clearQueue,
  currentSessionId,
  doneChunkRecievedRef,
  emit,
  endInactivityTimer,
  handleChangeSlide,
  handleInterrupt,
  handleTutorAction,
  isCanvasOpenRef,
  isPausedRef,
  resetClock,
  resumeFragmentFloorRef,
  setCurrentCaptions,
  setIsAITalking,
  setIsAwaitingTutorResponseRef,
  setTutorSessionStatus,
  startInactivityTimer,
  startTimeRef,
  subscribe,
}) {
  const runtimeRef = useLatestRef({
    abruptPauseTimerRef,
    addLog,
    clearQueue,
    currentSessionId,
    doneChunkRecievedRef,
    endInactivityTimer,
    handleChangeSlide,
    handleInterrupt,
    handleTutorAction,
    isCanvasOpenRef,
    isPausedRef,
    resetClock,
    resumeFragmentFloorRef,
    setCurrentCaptions,
    setIsAITalking,
    setIsAwaitingTutorResponseRef,
    setTutorSessionStatus,
    startInactivityTimer,
    startTimeRef,
  });

  const disconnectedMidResponseRef = useRef(false);
  const disconnectedSlideIndexRef = useRef(null);

  useEffect(() => {
    const openSilentClockIfMuted = () => {
      endSilentTurn();
      if (!useChatSettingsStore.getState().effectiveTutorAudioOn) {
        beginSilentTurn();
        // beginSilentTurn clears pausedAtMs, so a hold armed before the turn
        // existed does not survive into it -- and a muted turn has no audio
        // graph, making this clock the only thing holding its actions back.
        // Without this the drain watch can end the turn behind the hold and
        // strand every reveal it had left. Canvas turns share this handler and
        // are held too, which is the same rule: nothing animates over a slide
        // the client has not finished restoring.
        if (isResumePlaybackHeld()) pauseSilentTurn();
      }
    };

    const onCanvasTurnStart = openSilentClockIfMuted;
    const onCanvasAudioEnded = settleTutorTurn;
    // The canvas twin of the DONE handler below. A muted canvas turn streams
    // no audio, so the PCM player's onended — the only thing that normally
    // fires canvas:audio-playback-ended — never runs. No-op on audible turns.
    const onCanvasTurnDone = (event) => {
      if (isFromOtherTurn(event)) return;
      watchSilentTurnDrain(() =>
        window.dispatchEvent(new CustomEvent("canvas:audio-playback-ended")),
      );
    };
    // grpc/service.py falls back to audible TTS when a muted turn's mode
    // publication races or fails to land before the stream opens. If that
    // happens, real audio is already feeding the PCM player by the time it
    // arrives here, and its own onended is the only correct signal for when
    // this turn finishes — the silent clock's estimate must not race it.
    const onCanvasAudioChunk = () => endSilentTurn();
    window.addEventListener("canvas:turn-start", onCanvasTurnStart);
    window.addEventListener("canvas:audio-playback-ended", onCanvasAudioEnded);
    window.addEventListener("canvas:turn-done", onCanvasTurnDone);
    window.addEventListener("canvas:audio-chunk", onCanvasAudioChunk);

    const unsubStart = subscribe("START", () => {
      const {
        doneChunkRecievedRef,
        endInactivityTimer,
        isPausedRef,
        resetClock,
        setIsAITalking,
        setTutorSessionStatus,
      } = runtimeRef.current;
      addResumeDebugEvent("vodemo:event:START");
      resetClock();
      setIsAITalking(true);
      trackAiMessage();
      setTutorSessionStatus(TUTOR_SESSION_PHASE_STATUS.SPEAKING);
      window.animationController?.call("SPIRAL", { force: true });
      doneChunkRecievedRef.current = false;
      isPausedRef.current = false;
      endInactivityTimer();
      openSilentClockIfMuted();
    });

    const unsubDone = subscribe("DONE", () => {
      const { doneChunkRecievedRef } = runtimeRef.current;
      debugLog("DONE");
      addResumeDebugEvent("vodemo:event:DONE");
      doneChunkRecievedRef.current = true;
      watchSilentTurnDrain(() => emit("AUDIO_PLAYBACK_ENDED"));
    });

    const unsubAudioChunk = subscribe("AUDIO_CHUNK", () => {
      const {
        abruptPauseTimerRef,
        isPausedRef,
        setIsAwaitingTutorResponseRef,
        startTimeRef,
      } = runtimeRef.current;
      if (isPausedRef.current) {
        isPausedRef.current = false;
        const pauseDuration = Date.now() - abruptPauseTimerRef.current;
        startTimeRef.current += pauseDuration;
      }
      setIsAwaitingTutorResponseRef.current(false);
    });

    const unsubAudioPlaybackEnded = subscribe("AUDIO_PLAYBACK_ENDED", () => {
      const {
        isCanvasOpenRef,
        setIsAITalking,
        setIsAwaitingTutorResponseRef,
        setTutorSessionStatus,
        startInactivityTimer,
      } = runtimeRef.current;
      setIsAITalking(false);
      settleTutorTurn();
      setIsAwaitingTutorResponseRef.current(false);
      setTutorSessionStatus(TUTOR_SESSION_PHASE_STATUS.LISTENING);
      const notepadState = useNotepadStore.getState();
      if (!isCanvasOpenRef.current || notepadState.ingressType === "ai") {
        startInactivityTimer();
      }
      window.animationController?.call("SHRINK");
    });

    const unsubAudioUnderflow = subscribe("AUDIO_UNDERFLOW", () => {
      const { abruptPauseTimerRef, isPausedRef } = runtimeRef.current;
      isPausedRef.current = true;
      abruptPauseTimerRef.current = Date.now();
    });

    const unsubTutorActionReady = subscribe(
      "TUTOR_ACTION_READY",
      ({ action }) => {
        const { handleTutorAction, resumeFragmentFloorRef } =
          runtimeRef.current;
        debugLog("calling handleTutorAction with: ", action);
        let actionToHandle = action;
        const fragmentGuard = resumeFragmentFloorRef.current;
        const liveSlideIndex = useSlideStore.getState().currentSlideIndex;
        const guarded = applyResumeFragmentGuard(
          action,
          fragmentGuard,
          liveSlideIndex,
        );
        if (guarded) {
          if (
            guarded.filteredFragmentIds.length !==
            guarded.originalFragmentIds.length
          ) {
            addResumeDebugEvent("vodemo:fragment_guard:filtered", {
              original: guarded.originalFragmentIds,
              filtered: guarded.filteredFragmentIds,
              floor: fragmentGuard.fragmentFloor,
              slide_index: liveSlideIndex,
            });
          }

          if (guarded.dropped) {
            addResumeDebugEvent("vodemo:fragment_guard:dropped_action", {
              original: guarded.originalFragmentIds,
              floor: fragmentGuard.fragmentFloor,
              slide_index: liveSlideIndex,
            });
            return;
          }

          if (guarded.advanced) {
            resumeFragmentFloorRef.current = guarded.guard;
            addResumeDebugEvent("vodemo:fragment_guard:advance_floor", {
              next_floor: guarded.guard.fragmentFloor,
              slide_index: liveSlideIndex,
            });
          }

          actionToHandle = guarded.action;
        }

        addResumeDebugEvent("vodemo:event:TUTOR_ACTION_READY", {
          action_time: actionToHandle?.action_time,
          turn_id: actionToHandle?.turn_id,
          tutor_action: actionToHandle?.tutor_action,
        });
        handleTutorAction(actionToHandle);
      },
    );

    // WS_DISCONNECTED: immediately reset any stuck loading/speaking animation.
    // Without this, dots stay spinning if WS drops while the tutor was mid-response
    // (START had arrived but audio stream was cut before DONE arrived). The user
    // would have to press S to trigger an interrupt+SHRINK manually.
    //
    // Also clears buffered audio and captions so the UI doesn't appear frozen,
    // and tracks whether the tutor was mid-response so we can trigger recovery
    // on reconnect.
    const unsubWsDisconnected = subscribe("WS_DISCONNECTED", () => {
      const {
        clearQueue,
        isCanvasOpenRef,
        setCurrentCaptions,
        setIsAITalking,
        setTutorSessionStatus,
      } = runtimeRef.current;
      // Capture before clearing — isAITalking becomes false after setIsAITalking(false).
      // Re-explain if audio was playing when WS dropped, even if DONE arrived — the student
      // heard the response get cut off mid-playback and needs to hear it again.
      // Also capture the slide index so we can skip recovery if the student navigated away.
      disconnectedMidResponseRef.current = useAudioStore.getState().isAITalking;
      disconnectedSlideIndexRef.current =
        useSlideStore.getState().currentSlideIndex;
      // When the canvas is open, audio flows through the independent gateway WS —
      // don't kill canvas audio or reset talking state for a main WS drop.
      if (isCanvasOpenRef.current) {
        debugLog(
          "[WS_DISCONNECTED] Canvas is open — skipping audio/talking state cleanup",
        );
        return;
      }
      setIsAITalking(false);
      settleTutorTurn();
      setTutorSessionStatus(TUTOR_SESSION_PHASE_STATUS.LISTENING);
      if (disconnectedMidResponseRef.current) {
        window.animationController?.call("SHRINK");
      } else {
        window.animationController?.call("IDLE_TO_MESSAGE", { force: true });
      }
      window.pcmPlayer?.clear();
      setCurrentCaptions([]);
      clearQueue();
    });

    // WS_RECONNECTED: if audio was playing when WS dropped (tutor was speaking),
    // send RECONNECT_RECOVERY so the backend re-explains. This covers both the case
    // where DONE never arrived (partial generation) and where DONE arrived but playback
    // was cut short — in both cases the student heard an incomplete response.
    const unsubWsReconnected = subscribe("WS_RECONNECTED", () => {
      if (!disconnectedMidResponseRef.current) {
        // Tutor had already finished its turn — keep the speech bubble (MESSAGE state)
        // until the student speaks. USER_AUDIO_DONE force-sets state to LISTENING so
        // it works from MESSAGE without needing to go through IDLE first.
        return;
      }
      disconnectedMidResponseRef.current = false;
      // Don't trigger RECONNECT_RECOVERY when the canvas is open
      if (runtimeRef.current.isCanvasOpenRef.current) {
        debugLog(
          "[useVoDemoRuntimeEvents] WS reconnected but canvas is open — skipping RECONNECT_RECOVERY",
        );
        return;
      }
      const currentSlideIndex = useSlideStore.getState().currentSlideIndex;
      if (currentSlideIndex !== disconnectedSlideIndexRef.current) {
        debugLog(
          "[useVoDemoRuntimeEvents] WS reconnected but slide changed — skipping RECONNECT_RECOVERY",
        );
        return;
      }
      debugLog(
        "[useVoDemoRuntimeEvents] WS reconnected mid-response — triggering RECONNECT_RECOVERY",
      );
      emit("TURN_CHANGE", { source: "reconnect_recovery" });
      emit("RECONNECT_RECOVERY");
    });

    const unsubUserAudioDone = subscribe("USER_AUDIO_DONE", () => {
      const { isCanvasOpenRef, setIsAITalking, setTutorSessionStatus } =
        runtimeRef.current;
      if (isCanvasOpenRef.current) {
        return;
      }
      // Skip SPLIT+LOADING if the WS is down — DONE was never sent so the backend
      // won't respond. WS_DISCONNECTED already called SHRINK, so leave the animation.
      if (!useWebsocketStore.getState().wsConnected) {
        debugLog(
          "[USER_AUDIO_DONE] skipping LOADING — WS disconnected, DONE was not sent",
        );
        return;
      }
      debugLog("USER_AUDIO_DONE");
      if (window.animationController) {
        window.animationController.state = {
          value: 4,
          start: window.animationController.clock.getElapsedTime(),
        };
        window.animationController.call("SPLIT");
      }
      setIsAITalking(true);
      setTutorSessionStatus(TUTOR_SESSION_PHASE_STATUS.LOADING);
    });

    const unsubUpdateSlideSuccess = subscribe(
      "UPDATE_SLIDE_SUCCESS",
      (index) => {
        const { currentSessionId, handleChangeSlide } = runtimeRef.current;
        debugLog("UPDATE SLIDE SUCCESS");
        handleChangeSlide(currentSessionId, index);

        const slideContainer = document.getElementById("slide-container");
        if (slideContainer) {
          const visibleSlides = slideContainer.querySelectorAll(".visible");
          visibleSlides.forEach((slide) => {
            slide.classList.remove("visible");
            slide.classList.add("invisible");
          });
        }
      },
    );

    const unsubTurnChange = subscribe("TURN_CHANGE", (opts) => {
      const { endInactivityTimer, handleInterrupt } = runtimeRef.current;
      addResumeDebugEvent("vodemo:event:TURN_CHANGE");
      endSilentTurn();
      handleInterrupt({ keepExitModal: opts?.source === "inactivity" });
      setTimeout(() => {
        endInactivityTimer();
      }, 100);
    });

    const unsubLocalInterrupt = subscribe("LOCAL_INTERRUPT", () => {
      const { endInactivityTimer, handleInterrupt } = runtimeRef.current;
      addResumeDebugEvent("vodemo:event:LOCAL_INTERRUPT");
      settleTutorTurn();
      handleInterrupt();
      setTimeout(() => {
        endInactivityTimer();
      }, 100);
    });

    const unsubTurnChangeAndSlideUpdate = subscribe(
      "TURN_CHANGE_AND_SLIDE_UPDATE",
      (index) => {
        const { currentSessionId, handleChangeSlide, handleInterrupt } =
          runtimeRef.current;
        settleTutorTurn();
        handleInterrupt();
        setTimeout(() => {
          handleChangeSlide(currentSessionId, index);

          const slideContainer = document.getElementById("slide-container");
          if (slideContainer) {
            const visibleSlides = slideContainer.querySelectorAll(".visible");
            visibleSlides.forEach((slide) => {
              slide.classList.remove("visible");
              slide.classList.add("invisible");
            });
          }
        }, 100);
      },
    );

    const unsubReset = subscribe("RESET", () => {
      settleTutorTurn();
      const {
        addLog,
        clearQueue,
        endInactivityTimer,
        setCurrentCaptions,
        setIsAITalking,
        setTutorSessionStatus,
      } = runtimeRef.current;
      debugLog("[VoDemo] Received RESET - clearing stale state");
      addLog("[VoDemo] RESET received - clearing stale client state");
      addResumeDebugEvent("vodemo:event:RESET");

      const { clearAllAnnotations } = useAnnotationStore.getState();
      clearAllAnnotations();

      if (window.pcmPlayer && window.pcmPlayer.audioCtx) {
        window.pcmPlayer.clear();
      }

      setIsAITalking(false);
      setTutorSessionStatus(TUTOR_SESSION_PHASE_STATUS.LISTENING);

      setCurrentCaptions([]);
      clearQueue();

      endInactivityTimer();

      window.animationController?.call("SHRINK");
    });

    return () => {
      endSilentTurn();
      window.removeEventListener("canvas:turn-start", onCanvasTurnStart);
      window.removeEventListener(
        "canvas:audio-playback-ended",
        onCanvasAudioEnded,
      );
      window.removeEventListener("canvas:turn-done", onCanvasTurnDone);
      window.removeEventListener("canvas:audio-chunk", onCanvasAudioChunk);
      unsubStart();
      unsubDone();
      unsubAudioChunk();
      unsubAudioPlaybackEnded();
      unsubAudioUnderflow();
      unsubTutorActionReady();
      unsubWsDisconnected();
      unsubWsReconnected();
      unsubUserAudioDone();
      unsubUpdateSlideSuccess();
      unsubTurnChange();
      unsubLocalInterrupt();
      unsubTurnChangeAndSlideUpdate();
      unsubReset();
    };
  }, [runtimeRef, subscribe, emit]);
}
